import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { getAdminSalons, type AdminSalonRow } from "@/lib/admin.functions";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { formatEuro } from "@/lib/salonturbo";
import { ExternalLink, Loader2 } from "lucide-react";

export const Route = createFileRoute("/_authenticated/admin/salons")({
  component: AdminSalonsPage,
});

type SortKey = "created_at" | "name" | "coins_spent" | "balance" | "net_cents" | "last_boost";

const columns: Array<{ key: SortKey; label: string }> = [
  { key: "name", label: "Salon" },
  { key: "created_at", label: "Registriert" },
  { key: "balance", label: "Guthaben" },
  { key: "coins_spent", label: "Coins genutzt" },
  { key: "net_cents", label: "Umsatz netto" },
  { key: "last_boost", label: "Letzter Boost" },
];

function formatDate(value: string | null): string {
  if (!value) return "–";
  return new Date(value).toLocaleDateString("de-DE", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

function AdminSalonsPage() {
  const fetchSalons = useServerFn(getAdminSalons);
  const { data, isLoading } = useQuery({ queryKey: ["admin-salons"], queryFn: () => fetchSalons() });
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState<SortKey>("created_at");
  const [asc, setAsc] = useState(false);

  const rows = useMemo(() => {
    const list: AdminSalonRow[] = data ?? [];
    const term = search.trim().toLowerCase();
    const filtered = term
      ? list.filter((row) =>
          [row.name, row.city ?? "", row.owner_email ?? "", row.slug]
            .join(" ")
            .toLowerCase()
            .includes(term),
        )
      : list;
    return [...filtered].sort((a, b) => {
      const av = a[sort] ?? "";
      const bv = b[sort] ?? "";
      const cmp =
        typeof av === "number" && typeof bv === "number"
          ? av - bv
          : String(av).localeCompare(String(bv));
      return asc ? cmp : -cmp;
    });
  }, [data, search, sort, asc]);

  if (isLoading) {
    return (
      <div className="flex min-h-[30vh] items-center justify-center">
        <Loader2 className="size-6 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <Card className="rounded-3xl">
      <CardHeader className="gap-3">
        <div>
          <CardTitle className="text-lg font-light">Salons ({rows.length})</CardTitle>
          <CardDescription>Suche nach Name, Ort oder E-Mail. Spalten sind sortierbar.</CardDescription>
        </div>
        <Input
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Suchen…"
          className="max-w-sm rounded-2xl"
        />
      </CardHeader>
      <CardContent className="overflow-x-auto px-3 sm:px-6">
        <table className="w-full min-w-[880px] text-sm">
          <thead className="text-left text-xs uppercase tracking-wide text-muted-foreground">
            <tr>
              {columns.map((column) => (
                <th key={column.key} className="pb-2">
                  <button
                    type="button"
                    className="hover:text-foreground"
                    onClick={() => {
                      if (sort === column.key) setAsc((value) => !value);
                      else {
                        setSort(column.key);
                        setAsc(false);
                      }
                    }}
                  >
                    {column.label}
                    {sort === column.key ? (asc ? " ↑" : " ↓") : ""}
                  </button>
                </th>
              ))}
              <th className="pb-2">Status</th>
              <th className="pb-2">Seite</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.id} className="border-t align-top">
                <td className="py-3 pr-3">
                  <p className="font-medium">{row.name}</p>
                  <p className="text-xs text-muted-foreground">
                    {row.city ?? "–"} · {row.owner_email ?? "keine E-Mail"}
                  </p>
                </td>
                <td className="py-3 pr-3">{formatDate(row.created_at)}</td>
                <td className="py-3 pr-3">{row.balance}</td>
                <td className="py-3 pr-3">
                  {row.coins_spent}
                  <span className="block text-xs text-muted-foreground">
                    {row.coins_purchased} gekauft · {row.boost_count} Boost-Tage
                  </span>
                </td>
                <td className="py-3 pr-3">{formatEuro(row.net_cents)}</td>
                <td className="py-3 pr-3">{formatDate(row.last_boost)}</td>
                <td className="py-3 pr-3">
                  <Badge variant={row.status === "active" ? "default" : "secondary"}>
                    {row.status}
                  </Badge>
                  {row.campaign_id ? null : (
                    <span className="block pt-1 text-xs text-muted-foreground">ohne Kampagne</span>
                  )}
                </td>
                <td className="py-3">
                  <a
                    href={`/s/${row.slug}`}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1 text-primary hover:underline"
                  >
                    <ExternalLink className="size-3.5" />
                    öffnen
                  </a>
                </td>
              </tr>
            ))}
            {rows.length === 0 ? (
              <tr>
                <td colSpan={8} className="py-6 text-center text-muted-foreground">
                  Keine Salons gefunden.
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </CardContent>
    </Card>
  );
}
